<template>
  <div class="irp-key-contact" data-testid="irp-key-contact">
    <div class="irp-key-contact__partner">
      <BittsAvatar
        :org="partnerOrg"
        size="x-small"
        shape="square"
        :show-initials="false"
      />
      <span class="irp-key-contact__partner-name" data-testid="partner-name">
        {{ contact.partner_name }}
      </span>
    </div>
    <div class="irp-key-contact__body">
      <div class="irp-key-contact__details">
        <div class="irp-key-contact__identity">
          <RouterLink
            :to="contactRoute"
            class="irp-key-contact__name"
            data-testid="contact-name"
            @click="focusContact"
          >
            {{ contactName }}
          </RouterLink>
          <span
            v-if="contact.partner_contact_title"
            class="irp-key-contact__title"
          >
            {{ contact.partner_contact_title }}
          </span>
        </div>
        <InsightsTags
          :collapse="false"
          :partner-insights="contact.partner_insights"
          :global-insights="contact.insights"
        />
      </div>
      <AddToCrm
        :contact="addToCrmContact"
        :record-id="contactsStore.recordId ?? ''"
        :account="contactsStore.account"
        variant="ghost"
        add-text="Add to CRM"
        @added="contactsStore.refreshCopilotContactsStore()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsAvatar } from '@crossbeam/bitts';

import { computed } from 'vue';

import AddToCrm from '@/components/sales/AccountDrawer/components/ContactsTab/AddToCrm.vue';
import { addToCrmContactFromPartnerContact } from '@/components/sales/AccountDrawer/components/ContactsTab/addToCrmContact';
import InsightsTags from '@/components/sales/AccountDrawer/components/ContactsTab/InsightsTags.vue';

import {
  useCopilotContactsStore,
  useIndividualRecordStore,
  usePartnersStore,
} from '@/stores';
import { PartnerContact } from '@/types/copilotContacts';

import { EXPAND_ALL_QUERY, IRP_TAB, IRP_TAB_ROUTES } from './irp';
import { useRecordRouteParams } from './useRecordRouteParams';

const { contact } = defineProps<{
  contact: PartnerContact['first_contact'];
}>();

const contactsStore = useCopilotContactsStore();

const addToCrmContact = computed(() =>
  addToCrmContactFromPartnerContact(contact),
);

const partnerOrg = computed(() => ({
  name: contact.partner_name,
  logo_url: contact.partner_logo_url,
}));

const contactName = computed(
  () => contact.partner_contact_name || contact.partner_contact_email,
);

const individualRecordStore = useIndividualRecordStore();
const partnersStore = usePartnersStore();
const recordParams = useRecordRouteParams();

const contactRoute = computed(() => {
  const partnerOrgId = partnersStore.getPartnerOrgByUuid(
    contact.partner_id,
  )?.id;
  return {
    name: IRP_TAB_ROUTES[IRP_TAB.contacts],
    params: recordParams.value,
    query: partnerOrgId
      ? { partner_org_ids: String(partnerOrgId), ...EXPAND_ALL_QUERY }
      : EXPAND_ALL_QUERY,
  };
});

function focusContact() {
  individualRecordStore.focusContact(contact.partner_contact_email ?? null);
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-key-contact {
  @apply flex flex-col gap-4;
}

.irp-key-contact__partner {
  @apply flex items-center gap-8 min-w-0;
}

.irp-key-contact__partner-name {
  @apply text-sm font-bold text-neutral-text-strong truncate;
}

.irp-key-contact__body {
  @apply flex items-start justify-between gap-16;
}

.irp-key-contact__details {
  @apply flex flex-col gap-8 min-w-0;
}

.irp-key-contact__identity {
  @apply flex items-center flex-wrap gap-8 min-w-0;
}

.irp-key-contact__name {
  @apply font-bold text-neutral-text-strong hover:text-secondary-text-link-hover hover:underline;
}

.irp-key-contact__title {
  @apply text-neutral-text-weak;
}

.irp-key-contact :deep(.insight-tags) {
  @apply gap-4;
}
</style>
